/**
 * broadcastStore — Plan E E.4
 *
 * Zustand slice managing the multi-terminal broadcast feature:
 *   - enabled: master switch
 *   - members: Set<string> of terminal ids that participate in fan-out
 *   - socketRegistry: Map<string, WebSocket> for direct fan-out send (each
 *     TerminalTile registers its current WS instance; reconnect replaces it).
 *
 * Fan-out contract (E.G0 reviewer fix — echo-skip mandatory):
 *   When tile A's xterm.onData(data) fires AND broadcast.enabled AND
 *   broadcast.members.has(A.id):
 *     - send ('0' + data) to ws-A (the local one — always, regardless of broadcast)
 *     - send ('0' + data) to each ws-member WHERE member.id !== A.id
 *   Skipping self prevents echo (sending the keystroke twice to the originator's
 *   ws). The fan-out is one-way: A's keystroke goes to B and C; B's keystroke
 *   does NOT come back to A unless B is also a member and types itself.
 */
import { create } from 'zustand';

interface BroadcastState {
  enabled: boolean;
  members: Set<string>;
  socketRegistry: Map<string, WebSocket>;

  toggleEnabled: () => void;
  setEnabled: (enabled: boolean) => void;
  addMember: (id: string) => void;
  removeMember: (id: string) => void;
  toggleMember: (id: string) => void;
  registerSocket: (id: string, ws: WebSocket) => void;
  unregisterSocket: (id: string) => void;
  reset: () => void;
}

export const useBroadcastStore = create<BroadcastState>((set) => ({
  enabled: false,
  members: new Set<string>(),
  socketRegistry: new Map<string, WebSocket>(),

  toggleEnabled: () =>
    set((state) => ({ enabled: !state.enabled })),

  setEnabled: (enabled) =>
    set({ enabled }),

  addMember: (id) =>
    set((state) => {
      const next = new Set(state.members);
      next.add(id);
      return { members: next };
    }),

  removeMember: (id) =>
    set((state) => {
      const next = new Set(state.members);
      next.delete(id);
      return { members: next };
    }),

  toggleMember: (id) =>
    set((state) => {
      const next = new Set(state.members);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { members: next };
    }),

  registerSocket: (id, ws) =>
    set((state) => {
      const next = new Map(state.socketRegistry);
      next.set(id, ws);
      return { socketRegistry: next };
    }),

  unregisterSocket: (id) =>
    set((state) => {
      const next = new Map(state.socketRegistry);
      next.delete(id);
      return { socketRegistry: next };
    }),

  reset: () =>
    set({
      enabled: false,
      members: new Set<string>(),
      socketRegistry: new Map<string, WebSocket>(),
    }),
}));

/**
 * fanOut — pure helper called from TerminalTile's term.onData closure.
 *
 * Args:
 *   selfId — the originating tile's id
 *   data   — raw keystroke string from xterm.onData
 *   payload — pre-encoded bytes to send (Cmd.INPUT + data already prefixed)
 *
 * Contract: send `payload` to each ws in registry where the registry id is
 * different from selfId AND the ws is OPEN. Returns the count of successful
 * fan-out sends (for logging / tests). Does NOT send to self — that's the
 * caller's responsibility.
 */
export function fanOut(selfId: string, payload: Uint8Array): number {
  const state = useBroadcastStore.getState();
  if (!state.enabled) return 0;
  if (!state.members.has(selfId)) return 0;
  let sent = 0;
  state.socketRegistry.forEach((ws, memberId) => {
    if (memberId === selfId) return;
    if (ws.readyState !== WebSocket.OPEN) return;
    if (!state.members.has(memberId)) return; // member must be in broadcast set too
    ws.send(payload);
    sent += 1;
  });
  return sent;
}
